import React, { useCallback, useEffect, useState } from "react";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { orgApi } from "api/org";

const fieldStyle = {
  borderColor: "var(--color-border)",
  background: "var(--color-card)",
  color: "var(--color-foreground)",
};

/**
 * WhatsApp connection panel (Sprint 15 QA) — shared by Onboarding step 2 and
 * Settings. One place to: set the support phone, store per-org Meta
 * credentials (masked), see the webhook URL, test the connection.
 */
export default function WhatsAppConnection({ onSaved, compact = false }) {
  const [org, setOrg] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    whatsappPhone: "",
    whatsappPhoneId: "",
    whatsappAccessToken: "",
    whatsappVerifyToken: "",
    whatsappAppSecret: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const [orgData, statusData] = await Promise.all([orgApi.getOrg(), orgApi.getWhatsappStatus()]);
    setOrg(orgData);
    setStatus(statusData);
    setForm((f) => ({
      ...f,
      whatsappPhone: orgData?.whatsappPhone || "",
    }));
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e?.message || "Unable to load WhatsApp status."));
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const patch = {};
      if (form.whatsappPhone.trim() !== (org?.whatsappPhone || "")) patch.whatsappPhone = form.whatsappPhone.trim();
      if (form.whatsappPhoneId.trim() !== (org?.whatsappPhoneId || "")) patch.whatsappPhoneId = form.whatsappPhoneId.trim();
      if (form.whatsappAccessToken.trim()) patch.whatsappAccessToken = form.whatsappAccessToken.trim();
      if (form.whatsappVerifyToken.trim()) patch.whatsappVerifyToken = form.whatsappVerifyToken.trim();
      if (form.whatsappAppSecret.trim()) patch.whatsappAppSecret = form.whatsappAppSecret.trim();
      if (Object.keys(patch).length > 0) {
        await orgApi.updateOrg(patch);
      }
      setForm((f) => ({ ...f, whatsappAccessToken: "", whatsappVerifyToken: "", whatsappAppSecret: "" }));
      await load();
      await onSaved?.();
    } catch (e) {
      setError(e?.message || "Could not save the WhatsApp settings.");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!form.whatsappPhone.trim()) {
      setError("Save a WhatsApp number first, then test.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const result = await orgApi.testWhatsapp(form.whatsappPhone.trim());
      setTestResult(result);
    } catch (e) {
      setTestResult({ ok: false, detail: e?.message || "Test failed." });
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(status?.webhookUrl || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const isMeta = status?.provider === "meta";
  const connected = Boolean(status?.phone);

  const credField = (label, key, placeholder, hasFlag) => (
    <div>
      <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>
        {label}
      </label>
      <input
        type={key === "whatsappAccessToken" || key === "whatsappAppSecret" ? "password" : "text"}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={hasFlag ? "Saved — type to replace" : placeholder}
        className="w-full px-3 py-2 rounded-md border font-caption text-sm"
        style={fieldStyle}
        autoComplete="off"
      />
    </div>
  );

  return (
    <div>
      {error && (
        <div className="mb-3 rounded-lg border px-4 py-3 font-caption text-sm" style={{ borderColor: "var(--color-error)", background: "var(--color-card)", color: "var(--color-error)" }}>
          {error}
        </div>
      )}

      {/* Status */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {connected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: "#DCFCE7", color: "#15803D" }}>
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#15803D" }} />
            {status.phone}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: "#FEF3C7", color: "#B45309" }}>
            <Icon name="AlertTriangle" size={12} color="currentColor" />
            No number yet
          </span>
        )}
        {isMeta ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: "#DCFCE7", color: "#15803D" }}>
            <Icon name="CheckCircle2" size={12} color="currentColor" />
            Live — WhatsApp Business API
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: "#FEF3C7", color: "#B45309" }}>
            <Icon name="FlaskConical" size={12} color="currentColor" />
            Dev mode — add credentials to go live
          </span>
        )}
      </div>

      {/* Webhook URL */}
      <div className="mb-4 rounded-lg border px-3 py-2.5 flex items-center gap-2" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
        <Icon name="Link2" size={14} color="var(--color-text-secondary)" />
        <code className="font-caption text-xs flex-1 break-all" style={{ color: "var(--color-foreground)" }}>
          {status?.webhookUrl || "…"}
        </code>
        <button
          onClick={copyWebhook}
          className="inline-flex items-center gap-1 font-caption text-xs hover:underline"
          style={{ color: "var(--color-primary)", cursor: "pointer" }}
        >
          <Icon name={copied ? "Check" : "Copy"} size={13} color="currentColor" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>
            WhatsApp number (customers message this)
          </label>
          <input
            value={form.whatsappPhone}
            onChange={(e) => setForm((f) => ({ ...f, whatsappPhone: e.target.value }))}
            placeholder="+91 98765 43210"
            className="w-full px-3 py-2 rounded-md border font-caption text-sm"
            style={fieldStyle}
          />
        </div>
        {credField("Phone ID (Meta)", "whatsappPhoneId", "e.g. 123456789012345", Boolean(org?.whatsappPhoneId))}
        {credField("Access token (Meta)", "whatsappAccessToken", "Paste the permanent token", Boolean(org?.hasWhatsappAccessToken))}
        {credField("Webhook verify token", "whatsappVerifyToken", "The token you paste into Meta", Boolean(org?.hasWhatsappVerifyToken))}
        {credField("App secret (optional)", "whatsappAppSecret", "For signed webhook payloads", Boolean(org?.hasWhatsappAppSecret))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="default" size="sm" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save settings"}
        </Button>
        <Button variant="outline" size="sm" iconName="Send" onClick={test} disabled={testing || !form.whatsappPhone.trim()}>
          {testing ? "Sending..." : "Send test message"}
        </Button>
      </div>

      {testResult && (
        <div
          className="mt-3 rounded-lg border px-4 py-3 font-caption text-sm"
          style={{
            borderColor: testResult.ok ? "var(--color-success)" : "var(--color-error)",
            background: "var(--color-card)",
            color: testResult.ok ? "var(--color-success)" : "var(--color-error)",
          }}
        >
          {testResult.ok ? "Test message sent." : "Test failed:"} {testResult.detail}
        </div>
      )}

      {/* Guide */}
      {!compact && (
        <details className="mt-4 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <summary className="font-caption text-sm font-semibold px-4 py-3 cursor-pointer" style={{ color: "var(--color-foreground)" }}>
            How to connect the real WhatsApp Business API
          </summary>
          <div className="px-4 pb-4 font-caption text-sm space-y-2" style={{ color: "var(--color-text-secondary)" }}>
            <p>1. Create a Meta Business app and a WhatsApp Business account, then add the number above as a WhatsApp phone number.</p>
            <p>2. Copy the <strong>verify token</strong> you saved here and the <strong>webhook URL</strong> above into Meta: App Dashboard → WhatsApp → Configuration → Webhook.</p>
            <p>3. Generate a permanent <strong>access token</strong> (System User → WhatsApp Business Account) and paste it here with the <strong>Phone ID</strong> (WhatsApp Manager → API Setup).</p>
            <p>4. Subscribe to the <code className="text-xs">messages</code> field and click <strong>Send test message</strong> — a ping arrives on the number within seconds.</p>
            <p className="text-xs">Without credentials the app runs in <strong>dev mode</strong>: messages are logged instead of sent — perfect for demos.</p>
          </div>
        </details>
      )}
    </div>
  );
}